import { useState, useMemo } from "react";
import {
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaRegTimesCircle,
  FaSpinner,
  FaClock,
  FaPen,
} from "react-icons/fa";
import ClaimModal from "./ClaimModal";

const UnclaimedProfileTable = ({
  profiles,
  sortConfig,
  handleSort,
  onClaim,
  searchTerm,
  isRegexMode,
}) => {
  const [tooltip, setTooltip] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);

  const getBrandName = (brand) =>
    brand.friendly_name || brand.brand_name || brand.tcr_id || "N/A";

  const columns = [
    { key: "claim", label: "Claim", width: "12%" },
    { key: "friendly_name", label: "Customer Profile", width: "12%" },
    { key: "sid", label: "Profile SID", width: "20%" },
    { key: "policy_sid", label: "Policy SID", width: "20%" },
    { key: "status", label: "Status", width: "12%" },
    { key: "date_created", label: "Created At", width: "12%" },
    { key: "date_updated", label: "Updated At", width: "12%" },
  ];

  const profileStatusMapToIcon = {
    draft: <FaPen />,
    "pending-review": <FaClock color="yellow" />,
    "in-review": <FaSpinner />,
    "twilio-approved": <FaCheckCircle color="green" />,
    "twilio-rejected": <FaRegTimesCircle color="red" />,
  };

  const filteredProfiles = useMemo(() => {
    if (!searchTerm) return profiles;
    const regex = isRegexMode ? new RegExp(searchTerm, "i") : null;
    return profiles.filter((profile) => {
      const combinedFields = `${profile.sid} ${profile.friendly_name} ${profile.policy_sid} ${profile.status} ${profile.url}`;
      if (isRegexMode) {
        return regex.test(combinedFields);
      }
      return combinedFields.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [profiles, searchTerm, isRegexMode]);

  const handleMouseEnterStatus = (status, sid) => {
    let tooltipText = "";
    switch (status) {
      case "draft":
        tooltipText = "Profile is in draft state.";
        break;
      case "pending-review":
        tooltipText = "Profile is pending review by Twilio.";
        break;
      case "in-review":
        tooltipText = "Profile is currently being reviewed by Twilio.";
        break;
      case "twilio-approved":
        tooltipText = "Profile has been approved by Twilio.";
        break;
      case "twilio-rejected":
        tooltipText = "Profile has been rejected by Twilio.";
        break;
      default:
        tooltipText = "Unknown profile status.";
    }
    setTooltip({ text: tooltipText, sid: sid });
  };

  const handleMouseLeaveStatus = () => {
    setTooltip(null);
  };

  const handleClaimClick = (profile) => {
    setSelectedProfile(profile);
    setShowClaimModal(true);
  };

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-full">
      <table className="w-full table-fixed">
        <thead className="bg-gray-700 sticky top-0">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors duration-150 overflow-hidden"
                style={{ width: column.width }}
                onClick={() => {
                  if (column.key !== "claim") handleSort(column.key);
                }}
              >
                <div className="flex items-center justify-center">
                  {column.label}
                  {sortConfig.key === column.key && (
                    <span className="ml-2">
                      {sortConfig.direction === "ascending" ? (
                        <FaSortUp className="text-blue-400" />
                      ) : (
                        <FaSortDown className="text-blue-400" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {filteredProfiles.map((profile) => (
            <tr
              key={profile.sid}
              className="hover:bg-gray-700 transition-colors duration-150 cursor-pointer text-center overflow-x-auto"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <button
                  onClick={() => handleClaimClick(profile)}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-blue-500 transition-colors duration-150"
                >
                  Claim
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {profile.friendly_name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {profile.sid}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {profile.policy_sid}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                <div
                  className="flex items-center justify-center"
                  onMouseEnter={() =>
                    handleMouseEnterStatus(profile.status, profile.sid)
                  }
                  onMouseLeave={handleMouseLeaveStatus}
                >
                  {profileStatusMapToIcon[profile.status] ||
                    profile.status ||
                    "N/A"}
                </div>
                {tooltip && tooltip.sid === profile.sid && (
                  <div className="absolute bg-gray-800 text-white text-xs rounded px-2 mt-1">
                    {tooltip.text}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {profile.date_created}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 overflow-x-auto">
                {profile.date_updated}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showClaimModal && selectedProfile && (
        <ClaimModal
          profile={selectedProfile}
          onClose={() => setShowClaimModal(false)}
          onClaim={(claimPayload) => {
            onClaim(claimPayload);
            setShowClaimModal(false);
          }}
        />
      )}
    </div>
  );
};

export default UnclaimedProfileTable;
